import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LinkButton } from '@backstage/core-components';
import { makeStyles } from 'tss-react/mui';
import { HeroProps } from './types';
import { useMemo } from 'react';
import HeroBackground from '../../assets/hero-background.webp';

const useStyles = makeStyles()(() => ({
  button: {
    display: 'inline-flex',
  },
}));

export const Hero = ({
  title = 'Mercedes-Benz Tech-Insights Hub',
  subtitle,
  buttonLabel,
  buttonHref,
  forwardButton = false,
  backButton = false,
}: HeroProps) => {
  const { classes } = useStyles();
  const height = useMemo(() => {
    // Otherwise calculate based on content
    let calculatedHeight = { xs: 80, sm: 90, md: 100 };

    if (subtitle) {
      calculatedHeight = {
        xs: calculatedHeight.xs + 80,
        sm: calculatedHeight.sm + 90,
        md: calculatedHeight.md + 100,
      };
    }

    if (buttonLabel) {
      calculatedHeight = {
        xs: calculatedHeight.xs + 80,
        sm: calculatedHeight.sm + 90,
        md: calculatedHeight.md + 100,
      };
    }

    return calculatedHeight;
  }, [subtitle, buttonLabel]);

  // Determine which icon to use for the button
  const getButtonIcon = () => {
    if (forwardButton) {
      return <ArrowForwardIcon />;
    }
    if (backButton) {
      return <ArrowBackIcon />;
    }
    return null;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: {
          xs: typeof height.xs === 'number' ? `${height.xs}px` : height.xs,
          sm: typeof height.sm === 'number' ? `${height.sm}px` : height.sm,
          md: typeof height.md === 'number' ? `${height.md}px` : height.md,
        },
        backgroundImage: `linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.90) 30%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%), url(${HeroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: { xs: 3, md: 5 },
        marginBottom: '1rem',
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', md: '80%' },
          color: 'white',
          marginLeft: { xs: '0.1rem', md: '0.8rem' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 300,
            fontSize: {
              xs: '1.75rem',
              sm: '2.25rem',
              md: '2.75rem',
              lg: '3rem',
            },
            lineHeight: 1.2,
            mb: 2,
            wordBreak: 'break-word',
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mb: 4,
              maxWidth: '90%',
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {buttonLabel && (
          <LinkButton
            to={buttonHref}
            color="primary"
            variant="contained"
            endIcon={getButtonIcon()}
            className={classes.button}
            data-testid="hero-button"
          >
            {buttonLabel}
          </LinkButton>
        )}
      </Box>
    </Box>
  );
};
